/**
 * "A new version is available" — CLAUDE.md §6.5.
 *
 * **It never reloads by itself.** Someone is mid-filter, and taking the page away from them to
 * save a click is the reason people distrust software that updates itself. The offer sits under
 * the header, does not block anything, and can be dismissed.
 */

import { useState } from 'react';
import { faArrowRotateRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from '@/components/ui/button';
import { useBuildVersionMismatch } from '@/hooks/useBuildVersion';
import { useI18n } from '@/lib/i18n';

export function VersionPrompt() {
  const { text } = useI18n();
  const mismatch = useBuildVersionMismatch();
  const [dismissed, setDismissed] = useState(false);

  if (!mismatch || dismissed) {
    return null;
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-secondary px-4 py-2 text-secondary-foreground sm:px-6 lg:px-8"
    >
      <FontAwesomeIcon icon={faArrowRotateRight} className="text-sm" aria-hidden />
      <p className="text-sm">
        {text('A new version of WinSight is ready. Reload when it suits you — your filters are in the address bar, so nothing is lost.')}
      </p>
      <div className="ms-auto flex items-center gap-1">
        <Button
          size="sm"
          onClick={() => {
            window.location.reload();
          }}
        >
          {text('Reload')}
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={text('Dismiss the update notice')}
          onClick={() => {
            setDismissed(true);
          }}
        >
          <FontAwesomeIcon icon={faXmark} aria-hidden />
        </Button>
      </div>
    </div>
  );
}
